import "./Packages.scss";
import { useEffect, useState, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import Button, { ButtonType } from "../Button";
import ViewPackages from "./ViewPackages";
import MessageModal, { MessageModalType } from "../../components/modals/MessageModal";
import { ResellerPackageType } from "../../features/Resellers/PackageType";
import { objectsEqual } from "../../utils/validation";

const fieldVisibilityByType = {
	[ResellerPackageType.Tiered3]: {
		seatsType: true,
		seatsPerPackage: true,
		bronzePrice: true,
		silverPrice: true,
		goldPrice: true,
		platinumPrice: false,
		onboardingPrice: false,
		price: false,
		upsellPrice: false
	},
	[ResellerPackageType.Tiered4]: {
		seatsType: true,
		seatsPerPackage: true,
		bronzePrice: true,
		silverPrice: true,
		goldPrice: true,
		platinumPrice: true,
		onboardingPrice: false,
		price: false,
		upsellPrice: false
	},
	[ResellerPackageType.Single]: {
		seatsType: true,
		seatsPerPackage: true,
		bronzePrice: false,
		silverPrice: false,
		goldPrice: false,
		platinumPrice: false,
		price: true,
		upsellPrice: false
	},
	[ResellerPackageType.SingleUnlimitedUsers]: {
		seatsType: false,
		seatsPerPackage: false,
		bronzePrice: false,
		silverPrice: false,
		goldPrice: false,
		platinumPrice: false,
		onboardingPrice: true,
		price: true,
		upsellPrice: false
	},
	[ResellerPackageType.Uncosted]: {
		seatsType: true,
		seatsPerPackage: true,
		bronzePrice: false,
		silverPrice: false,
		goldPrice: false,
		platinumPrice: false,
		onboardingPrice: false,
		price: false,
		upsellPrice: false
	},
	[ResellerPackageType.IndependentUpsells]: {
		seatsType: true,
		seatsPerPackage: true,
		bronzePrice: false,
		silverPrice: false,
		goldPrice: false,
		platinumPrice: false,
		onboardingPrice: false,
		price: true,
		upsellPrice: true
	}
};

const seatTypes = [
	{ label: "PUP", value: true },
	{ label: "PCP", value: false }
];

const AddPackage = ({ selectedPackages = [], availablePackages = [], setSelectedPackages, isValid }) => {
	const INITIAL_DATA = {
		pup: null,
		seatsPerPackage: null,
		bronzePrice: null,
		silverPrice: null,
		goldPrice: null,
		platinumPrice: null,
		onboardingPrice: null,
		price: null,
		upsellPrice: null
	};
	const [selectedPackage, setSelectedPackage] = useState(null);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [packageToRemove, setPackageToRemove] = useState(false);
	const [formData, setFormData] = useState(INITIAL_DATA);
	const initialPackages = useRef(selectedPackages);

	useEffect(() => {
		if (typeof isValid === "function") {
			const hasChanges = !objectsEqual(initialPackages.current, selectedPackages)
			isValid(hasChanges && selectedPackages.length > 0);
		}
	}, [selectedPackages]);

	const handleInputChange = (key, value) => {
		setFormData(prev => {
			const updated = { ...prev, [key]: value };

			if (key === "pup") {
				if (value === true) {
					updated.seatsPerPackage = 1;
				}
				else if (prev.pup === true) {
					updated.seatsPerPackage = null;
				}
			}

			return updated;
		});
	};

	const getVisibleFields = () => {
		if (!selectedPackage) {
			return {};
		}
		return fieldVisibilityByType[selectedPackage.PackageType] ?? {};
	};

	const handleConfirmDelete = (idToRemove) => {
		setPackageToRemove(idToRemove);
		setConfirmDelete(true);
	};

	const handleRemovePackage = (yes) => {
		if (yes) {
			setSelectedPackages(prev => prev.filter(pkg => pkg.Id !== packageToRemove));
		}
		setConfirmDelete(false);
	};

	const allVisibleFieldsFilled = () => {
		const visible = getVisibleFields();
		return Object.entries(visible).every(([key, isVisible]) => {
			if (!isVisible) {
				return true;
			}
			return formData[key] !== null && formData[key] !== "";
		});
	};

	const handleAddPackage = () => {
		if (!selectedPackage) {
			return;
		}

		const newPackage = {
			Id: -1,
			ParentId: selectedPackage.ParentId,
			PackageId: selectedPackage.PackageId,
			Name: selectedPackage.PackageName,
			PUP: formData.pup,
			SeatBlockSize: formData.seatsPerPackage,
			BronzePrice: formData.bronzePrice,
			SilverPrice: formData.silverPrice,
			GoldPrice: formData.goldPrice,
			PlatinumPrice: formData.platinumPrice,
			OnboardingPrice: formData.onboardingPrice,
			Price: formData.price,
			UpsellPrice: formData.upsellPrice,
			PackageType: selectedPackage.PackageType,
			CanDelete: true
		};

		setSelectedPackages(prev => [...prev, newPackage]);
		setSelectedPackage(null);
		setFormData(INITIAL_DATA);
	};

	return (
		<div className="add-package">
			<h4>Add Package</h4>
			<p>Select a package from the dropdown below to begin adding it.</p>
			<div className="add-package-dropdown">
				<Dropdown
					value={selectedPackage}
					options={availablePackages}
					optionLabel="DisplayName"
					placeholder="Select a Package"
					onChange={(e) => {
						const packageType = e.value.PackageType;
						const isUnlimited = packageType === ResellerPackageType.SingleUnlimitedUsers;

						setSelectedPackage(e.value);
						setFormData(prev => ({
							...prev,
							name: e.value.DisplayName,
							seatsPerPackage: isUnlimited ? 99999 : null
						}));
					}}
					className="mb-3"
				/>
			</div>

			{selectedPackage && (() => {
				const visible = getVisibleFields();

				return (
					<>
						<h5>Add Package Details</h5>
						<div className="add-package-grid">
							{visible.seatsType && (
								<div className="form-cell">
									<label>Seats Type</label>
									<Dropdown
										value={formData.pup}
										options={seatTypes}
										onChange={(e) => handleInputChange("pup", e.value)}
										placeholder="Select Seats Type"
									/>
								</div>
							)}

							{visible.seatsPerPackage && formData.pup === false && (
								<div className="form-cell">
									<label>Seats per Package</label>
									<InputNumber
										value={formData.seatsPerPackage}
										onValueChange={(e) => handleInputChange("seatsPerPackage", e.value)}
										min={1}
									/>
								</div>
							)}

							{visible.bronzePrice && (
								<div className="form-cell">
									<label>Bronze Price</label>
									<InputNumber
										value={formData.bronzePrice}
										onValueChange={(e) => handleInputChange("bronzePrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.silverPrice && (
								<div className="form-cell">
									<label>Silver Price</label>
									<InputNumber
										value={formData.silverPrice}
										onValueChange={(e) => handleInputChange("silverPrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.goldPrice && (
								<div className="form-cell">
									<label>Gold Price</label>
									<InputNumber
										value={formData.goldPrice}
										onValueChange={(e) => handleInputChange("goldPrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.platinumPrice && (
								<div className="form-cell">
									<label>Platinum Price</label>
									<InputNumber
										value={formData.platinumPrice}
										onValueChange={(e) => handleInputChange("platinumPrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.onboardingPrice && (
								<div className="form-cell">
									<label>Onboarding Price</label>
									<InputNumber
										value={formData.onboardingPrice}
										onValueChange={(e) => handleInputChange("onboardingPrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.price && (
								<div className="form-cell">
									<label>Price</label>
									<InputNumber
										value={formData.price}
										onValueChange={(e) => handleInputChange("price", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}

							{visible.upsellPrice && (
								<div className="form-cell">
									<label>Upsell Price</label>
									<InputNumber
										value={formData.upsellPrice}
										onValueChange={(e) => handleInputChange("upsellPrice", e.value)}
										mode="currency"
										currency="GBP"
										locale="en-GB"
										min={0}
									/>
								</div>
							)}
						</div>
						<div className="add-package-buttons">
							<Button
								type={ButtonType.Primary}
								label="Add Package"
								onClick={handleAddPackage}
								disabled={!allVisibleFieldsFilled()}
							/>
						</div>
					</>
				);
			})()}

			{selectedPackages.length > 0 && (
				<ViewPackages
					selectedPackages={selectedPackages}
					onRemovePackage={handleConfirmDelete}
				/>
			)}

			{confirmDelete &&
				<MessageModal title="Confirm Delete" type={MessageModalType.Question} onClick={handleRemovePackage} size="m">
					This will remove the package. Are you sure you want to continue?
				</MessageModal>
			}
		</div>
	);
};

export default AddPackage;
